import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Slider } from './Slider';
import SEO from './SEO';
import DailyDish from './DailyDish';
import { Container, Grid, Paper, Typography, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import axiosInstance, { API_ENDPOINTS } from '../axios/axiosInstance';
import FormProvider, { RHFCheckbox, RHFTextField, RHFTextFieldMUI } from './hook-form';
import RHFDateTimePicker from './hook-form/rhf-datetime-picker';
import moment from 'moment/moment';
import LoadingButton from '@mui/lab/LoadingButton';
import Turnstile from './Turnstile';


const Home = () => {
  const { t, i18n } = useTranslation();
  const [reservationTurnstileToken, setReservationTurnstileToken] = useState(null);
  const [contactTurnstileToken, setContactTurnstileToken] = useState(null);

  // Reservation form
  const reservationSchema = Yup.object().shape({
    fullName: Yup.string().required(t('reservations.requiredField')),
    email: Yup.string().required(t('reservations.requiredField')),
    phoneNumber: Yup.string().required(t('reservations.requiredField')),
    personsAmmount: Yup.string().required(t('reservations.requiredField')),
    date: Yup.string().required(t('reservations.requiredField')),
    notes: Yup.string(),
    terms: Yup.bool().oneOf([true], t('reservations.requiredField'))
  });

  const reservationDefaults = useMemo(() => ({
    fullName: '',
    email: '',
    phoneNumber: '',
    personsAmmount: '',
    date: '',
    notes: '',
    terms: false
  }), []);

  const reservationMethods = useForm({
    resolver: yupResolver(reservationSchema),
    defaultValues: reservationDefaults,
  });

  const { setValue: setReservationValue, reset: resetReservation, getValues: getReservationValues, handleSubmit: handleReservationSubmit, formState: { isSubmitting: isReservationSubmitting } } = reservationMethods;

  const onReservationSubmit = useCallback(
    async (data) => {
      try {
        if (!reservationTurnstileToken) {
          enqueueSnackbar('Por favor, complete a verificação de segurança.', { variant: 'error' });
          return;
        }
        const isoDate = moment(data.date).format();
        const objToSend = {
          ...getReservationValues(),
          date: isoDate,
          language: i18n.language,
          turnstileToken: reservationTurnstileToken
        };
        console.log('Sending reservation:', objToSend);
        await axiosInstance.post(API_ENDPOINTS.reservation, objToSend);
        enqueueSnackbar(t('reservations.successMessage'), {
          variant: 'success',
          autoHideDuration: 12000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
        resetReservation();
        setReservationTurnstileToken(null);
      } catch (error) {
        console.error(error);
      }
    },
    [resetReservation, getReservationValues, i18n.language, t, reservationTurnstileToken]
  );

  const handlePersonsChange = (event) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    setReservationValue('personsAmmount', numericValue);
  };

  // Contact form
  const contactSchema = Yup.object().shape({
    fullName: Yup.string().required(t('reservations.requiredField')),
    email: Yup.string().required(t('reservations.requiredField')),
    message: Yup.string().required(t('reservations.requiredField')),
    terms: Yup.bool().oneOf([true], t('reservations.requiredField'))
  });

  const contactDefaults = useMemo(() => ({
    fullName: '',
    email: '',
    message: '',
    terms: false
  }), []);

  const contactMethods = useForm({
    resolver: yupResolver(contactSchema),
    defaultValues: contactDefaults,
  });

  const { reset: resetContact, handleSubmit: handleContactSubmit, formState: { isSubmitting: isContactSubmitting } } = contactMethods;

  const onContactSubmit = useCallback(
    async (data) => {
      try {
        if (!contactTurnstileToken) {
          enqueueSnackbar('Por favor, complete a verificação de segurança.', { variant: 'error' });
          return;
        }
        data.turnstileToken = contactTurnstileToken;
        await axiosInstance.post(API_ENDPOINTS.contact, data);
        enqueueSnackbar(t('contacts.successMessage'), { variant: 'success' });
        resetContact();
        setContactTurnstileToken(null);
      } catch (error) {
        console.error(error);
      }
    },
    [resetContact, t, contactTurnstileToken]
  );

  const testimonials = [
    { name: t('home.testimonial1Name'), text: t('home.testimonial1Text') },
    { name: t('home.testimonial2Name'), text: t('home.testimonial2Text') },
    { name: t('home.testimonial3Name'), text: t('home.testimonial3Text') },
  ];

  const services = [
    { icon: 'mdi:silverware-fork-knife', title: t('home.serviceRestaurant'), desc: t('home.serviceRestaurantDesc') },
    { icon: 'mdi:food-steak', title: t('home.serviceRodizio'), desc: t('home.serviceRodizioDesc') },
    { icon: 'mdi:fish', title: t('home.serviceSeafood'), desc: t('home.serviceSeafoodDesc') },
    { icon: 'mdi:party-popper', title: t('home.serviceEvents'), desc: t('home.serviceEventsDesc') },
  ];

  return (
    <>
      <SEO
        titleKey="seo.homeTitle"
        descriptionKey="seo.homeDescription"
        path="/"
      />
      <Slider />

      <DailyDish />

      <div id="content">
        {/* Welcome Section */}
        <section className="padding-100 chef-style-2 text-center">
          <div className="container">
            <div className='row'>
              <div className='col-md-12'>
                <div className="head_title">
                  <h1>Fleur de Sel</h1>
                  <span className="welcome">{t('home.welcome')}</span>
                </div>
                <h3>{t('home.title')}</h3>
                <p>{t('home.description')}</p>
              </div>
            </div>
            <div className='row' style={{ marginTop: '40px' }}>
              <div className='col-md-4'><img className="img-responsive" src="img/inicio/1.jpg" alt="Interior do restaurante Fleur de Sel Luxembourg" /></div>
              <div className='col-md-4'><img className="img-responsive" src="img/inicio/2.jpg" alt="Pratos de marisco fresco no Fleur de Sel" /></div>
              <div className='col-md-4'><img className="img-responsive" src="img/inicio/3.jpg" alt="Ambiente acolhedor do restaurante português em Luxembourg" /></div>
            </div>
            <div className='row' style={{ marginTop: '40px' }}>
              <div className='col-md-12'>
                <Link
                  to="/gallery"
                  className="btn btn-default"
                  style={{
                    background: '#7e7b5e',
                    color: '#fff',
                    padding: '12px 30px',
                    borderRadius: '0',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    fontSize: '13px',
                    fontWeight: '400',
                    border: 'none'
                  }}
                >
                  {t('home.viewGallery')}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section style={{ backgroundColor: '#f9f8f4', padding: '80px 0' }}>
          <div className="container">
            <div className="head_title text-center">
              <h2>{t('home.ourServices')}</h2>
              <span className="welcome">{t('home.servicesSubtitle')}</span>
            </div>
            <div className="row" style={{ marginTop: '50px' }}>
              {services.map((service, index) => (
                <div key={index} className="col-md-3 col-sm-6" style={{ marginBottom: '30px' }}>
                  <div style={{ textAlign: 'center', padding: '30px 20px' }}>
                    <Icon icon={service.icon} width={60} style={{ color: '#7e7b5e', marginBottom: '20px' }} />
                    <h4 style={{ fontSize: '18px', fontWeight: '500', marginBottom: '15px', color: '#333' }}>
                      {service.title}
                    </h4>
                    <p style={{ fontSize: '14px', color: '#777', lineHeight: '1.6' }}>
                      {service.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center" style={{ marginTop: '20px' }}>
              <Link
                to="/events"
                className="btn btn-default"
                style={{
                  background: '#7e7b5e',
                  color: '#fff',
                  padding: '12px 30px',
                  borderRadius: '0',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  fontSize: '13px',
                  fontWeight: '400',
                  border: 'none'
                }}
              >
                {t('home.viewEvents')}
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="head_title text-center">
              <h2>{t('home.testimonials')}</h2>
              <span className="welcome">{t('home.testimonialsSubtitle')}</span>
            </div>
            <div className="row" style={{ marginTop: '50px' }}>
              {testimonials.map((testimonial, index) => (
                <div key={index} className="col-md-4" style={{ marginBottom: '30px' }}>
                  <div style={{
                    backgroundColor: '#fff',
                    padding: '30px',
                    borderRadius: '5px',
                    boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
                    height: '100%'
                  }}>
                    <div style={{ display: 'flex', marginBottom: '15px' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Icon key={star} icon="mdi:star" width={20} style={{ color: '#f5a623' }} />
                      ))}
                    </div>
                    <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic', lineHeight: '1.7', marginBottom: '20px' }}>
                      "{testimonial.text}"
                    </p>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#333', margin: 0 }}>
                      - {testimonial.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center" style={{ marginTop: '20px' }}>
              <a
                href="https://www.google.com/search?q=Fleur+De+Sel+Cr%C3%ADticas&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxIxNDU3NbE0szQ3M7QwNjU0NzY2s9jAyPiKUcwtJ7W0SMElVSE4NUfBuejw2pLM5MTiRaw4JADfaeK5TQAAAA&rldimm=15754969761835173368&tbm=lcl&hl=pt-PT#"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-default"
                style={{
                  background: '#7e7b5e',
                  color: '#fff',
                  padding: '12px 30px',
                  borderRadius: '0',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  fontSize: '13px',
                  fontWeight: '400',
                  border: 'none'
                }}
              >
                {t('home.writeReview')}
              </a>
            </div>
          </div>
        </section>

        {/* Reservation Section */}
        <section style={{
          backgroundImage: 'url(img/menu/banner.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          padding: '80px 0',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}></div>
          <Container maxWidth="md" style={{ position: 'relative', zIndex: 1 }}>
            <div className="head_title text-center" style={{ marginBottom: '40px' }}>
              <h2 style={{ color: '#fff' }}>{t('home.makeReservation')}</h2>
              <span style={{ color: 'rgba(255,255,255,0.8)' }}>{t('home.reservationSubtitle')}</span>
            </div>
            <Paper sx={{ p: { xs: 2, md: 4 } }}>
              <FormProvider methods={reservationMethods} onSubmit={handleReservationSubmit(onReservationSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <RHFTextField name="fullName" label={t('reservations.fullName')} fullWidth inputProps={{ autoComplete: 'off' }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <RHFTextField name="email" label={t('reservations.email')} fullWidth inputProps={{ autoComplete: 'off' }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <RHFTextFieldMUI name="phoneNumber" label={t('reservations.phone')} fullWidth variant='outlined' autoFormat defaultCountry={'lu'} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <RHFTextField type='text' name="personsAmmount" label={t('reservations.persons')} onChange={handlePersonsChange} fullWidth inputProps={{ pattern: '[0-9]*', maxLength: 2 }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <RHFDateTimePicker slotProps={{ textField: { fullWidth: true } }} name="date" ampm={false} />
                  </Grid>
                  <Grid item xs={12}>
                    <RHFTextField name="notes" multiline rows={2} label={t('reservations.notes')} fullWidth />
                  </Grid>
                  <Grid item xs={12}>
                    <Turnstile
                      onVerify={(token) => setReservationTurnstileToken(token)}
                      onExpire={() => setReservationTurnstileToken(null)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <RHFCheckbox name="terms" label={
                      <Typography style={{ fontSize: '12px' }}>
                        {t('reservations.terms')}{' '}
                        <Link to="/terms-and-conditions" style={{ fontWeight: 'bold' }} className='active' target="_blank">
                          {t('reservations.termsLink')}
                        </Link>.
                      </Typography>
                    } />
                  </Grid>
                  <Grid item xs={12} sm={4} style={{ textAlign: 'right' }}>
                    <LoadingButton loading={isReservationSubmitting} disabled={!reservationTurnstileToken} type='submit' fullWidth variant="contained" className='white-color' size="large">
                      {t('reservations.submit')}
                    </LoadingButton>
                  </Grid>
                </Grid>
              </FormProvider>
            </Paper>
          </Container>
        </section>

        {/* Contact Section */}
        <section style={{ padding: '80px 0' }}>
          <Container maxWidth="md">
            <div className="head_title text-center" style={{ marginBottom: '40px' }}>
              <h2>{t('home.contactUs')}</h2>
              <span className="welcome">{t('home.contactSubtitle')}</span>
            </div>
            <Paper sx={{ p: { xs: 2, md: 4 }, boxShadow: '0 2px 15px rgba(0,0,0,0.08)' }}>
              <FormProvider methods={contactMethods} onSubmit={handleContactSubmit(onContactSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <RHFTextField name="fullName" label={t('contacts.fullName')} fullWidth inputProps={{ autoComplete: 'off' }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <RHFTextField name="email" label={t('contacts.email')} fullWidth inputProps={{ autoComplete: 'off' }} />
                  </Grid>
                  <Grid item xs={12}>
                    <RHFTextField name="message" multiline rows={3} label={t('contacts.message')} fullWidth />
                  </Grid>
                  <Grid item xs={12}>
                    <Turnstile
                      onVerify={(token) => setContactTurnstileToken(token)}
                      onExpire={() => setContactTurnstileToken(null)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <RHFCheckbox name="terms" label={
                      <Typography style={{ fontSize: '12px' }}>
                        {t('contacts.terms')}{' '}
                        <Link to="/terms-and-conditions" style={{ fontWeight: 'bold' }} className='active' target="_blank">
                          {t('contacts.termsLink')}
                        </Link>.
                      </Typography>
                    } />
                  </Grid>
                  <Grid item xs={12} sm={4} style={{ textAlign: 'right' }}>
                    <LoadingButton loading={isContactSubmitting} disabled={!contactTurnstileToken} type='submit' fullWidth variant="contained" className='white-color' size="large">
                      {t('contacts.submit')}
                    </LoadingButton>
                  </Grid>
                </Grid>
              </FormProvider>
            </Paper>
          </Container>
        </section>

        {/* Contact Info Section */}
        <section style={{ backgroundColor: '#f9f8f4', padding: '60px 0' }}>
          <Container maxWidth="md">
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={6} sm={3}>
                <a href="https://www.facebook.com/profile.php?id=100063786393603" target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block', textAlign: 'center' }}>
                  <Icon icon="ic:baseline-facebook" width={40} style={{ color: '#7e7b5e' }} />
                  <h4 style={{ fontSize: '14px', marginTop: '10px' }}>{t('contacts.facebook')}</h4>
                </a>
              </Grid>
              <Grid item xs={6} sm={3}>
                <div style={{ textAlign: 'center' }}>
                  <Icon icon="ic:baseline-location-on" width={40} style={{ color: '#7e7b5e' }} />
                  <h4 style={{ fontSize: '14px', marginTop: '10px' }}>{t('contacts.address')}</h4>
                  <address style={{ fontSize: '12px', color: '#666' }}>25 rue Joseph Junck<br />Luxembourg</address>
                </div>
              </Grid>
              <Grid item xs={6} sm={3}>
                <a href="mailto:reservations@fleurdeselrestaurant.lu" style={{ textDecoration: 'none', color: 'inherit', display: 'block', textAlign: 'center' }}>
                  <Icon icon="ic:baseline-email" width={40} style={{ color: '#7e7b5e' }} />
                  <h4 style={{ fontSize: '14px', marginTop: '10px' }}>{t('contacts.emailLabel')}</h4>
                </a>
              </Grid>
              <Grid item xs={6} sm={3}>
                <a href="tel:+35228371979" style={{ textDecoration: 'none', color: 'inherit', display: 'block', textAlign: 'center' }}>
                  <Icon icon="ic:baseline-phone" width={40} style={{ color: '#7e7b5e' }} />
                  <h4 style={{ fontSize: '14px', marginTop: '10px' }}>{t('contacts.phone')}</h4>
                  <span style={{ fontSize: '12px', color: '#666' }}>(+352) 28 37 19 79</span>
                </a>
              </Grid>
            </Grid>
          </Container>
        </section>
      </div>
    </>
  );
};

export default Home;
